// src/components/ReportErrorBoundary.jsx
//
// Scoped error boundary for individual report views (task #514).
// Wraps each report page so a bad render inside one report is isolated
// to that content area — the nav rail and the rest of the shell stay
// usable and the user can navigate away without a hard-refresh.
//
// Intentionally separate from the top-level ErrorBoundary (task #426),
// which remains the last-resort catcher for crashes outside any report.
// The two boundaries form a nested pair: outer catches app-wide errors,
// inner catches per-report errors before they propagate outward.
//
// Class component — React error boundaries require getDerivedStateFromError
// / componentDidCatch, which are class-only lifecycle methods.

import React from "react";
import { T } from "../app/tokens.js";
import { log } from "../utils/logger.js";

export class ReportErrorBoundary extends React.Component {
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
    this.handleReset = this.handleReset.bind(this);
  }

  componentDidCatch(error, info) {
    log.error("ReportErrorBoundary caught unhandled render error", { name: error?.name, message: error?.message });
  }

  handleReset() {
    this.setState({ hasError: false, error: null });
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div
        data-testid="report-error-boundary-fallback"
        role="alert"
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: "40px 28px",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            background: T.card,
            border: `1px solid ${T.border}`,
            borderRadius: 12,
            padding: "32px 40px",
            maxWidth: 480,
            width: "100%",
            textAlign: "center",
            boxShadow: T.shadowCardXs,
          }}
        >
          <div style={{ fontSize: T.fs2xl, marginBottom: 12 }}>⚠️</div>
          <h2
            style={{
              fontSize: T.fsLg5,
              fontWeight: T.fwBold,
              color: T.dark,
              margin: "0 0 8px",
            }}
          >
            This report couldn&apos;t load
          </h2>
          <p
            style={{
              fontSize: T.fsBase,
              color: T.mid,
              margin: "0 0 20px",
              lineHeight: 1.6,
            }}
          >
            An unexpected error occurred while rendering this report. Use the
            nav to go to another page, or try again below.
          </p>
          <button
            type="button"
            data-testid="report-error-boundary-retry"
            onClick={this.handleReset}
            style={{
              background: T.blue,
              color: T.card,
              border: "none",
              borderRadius: 6,
              padding: "9px 22px",
              fontSize: T.fsBase,
              fontWeight: T.fwSemibold,
              cursor: "pointer",
            }}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }
}
